<template>
  <div>
    <b-form-group>
      <label :for="id">{{ label }}</label>
      <validation-provider
        #default="{ errors }"
        :name="errorName"
        :rules="rules"
        :vid="vid"
      >
        <input
          :id="id"
          v-model="displayValue"
          type="text"
          class="form-control"
          :placeholder="placeholder"
          :disabled="readonly"
          autocomplete="off"
          @input="handleInput"
          @blur="handleBlur"
          @keypress="handleKeyPress"
          @keydown="disableCopyPaste"
        >
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>
  </div>
</template>

<script>
import { BFormGroup } from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { removeSpecialChar, removeNegativeNumber } from '../../helpers/handleSpecialCharacter'

export default {
  name: 'SpecialInputNumberField',

  components: {
    BFormGroup,
    ValidationProvider,
  },
  mixins: [sweetAlert],
  props: {
    label: {
      type: String,
      required: true,
    },
    errorName: {
      type: String,
      default: 'default',
    },
    rules: {
      type: [Object, String],
      default: '',
    },
    id: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    value: {
      type: null,
      default: '',
    },
    onInput: {
      type: Function,
      default: null,
      required: false,
    },
    vid: {
      type: String,
      default: 'input-number',
    },
    isAllowSpecialCharacters: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAllowCopyAndPaste: {
      type: Boolean,
      default: true,
      required: false,
    },
    isAllowNegativeNumbers: {
      type: Boolean,
      default: true,
      required: false,
    },
    decimalPlaces: {
      type: Number,
      default: 2,
      required: false,
    },
  },
  data() {
    return {
      displayValue: '',
      internalValue: null,
      isFocused: false,
    }
  },
  computed: {
    hasValue() {
      return !!this.internalValue
    },
  },
  watch: {
    value(val) {
      if (val !== this.internalValue) {
        this.internalValue = val
        this.displayValue = this.formatDisplayValue(val)
      }
    },
  },
  created() {
    if (this.value !== null && this.value !== undefined && this.value !== '') {
      this.internalValue = this.value
      this.displayValue = this.formatDisplayValue(this.value)
    }
  },
  methods: {
    formatDisplayValue(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }

      const numValue = parseFloat(value)
      if (Number.isNaN(numValue)) {
        return ''
      }

      // Check if the number is a whole number (no decimal part)
      const isWholeNumber = numValue % 1 === 0

      let formatted
      if (isWholeNumber) {
        // For whole numbers, don't show decimal places
        formatted = Math.abs(numValue).toString()
      } else {
        // For decimal numbers, show trailing zeros
        formatted = Math.abs(numValue).toFixed(this.decimalPlaces)
      }

      // Add thousand separators
      const parts = formatted.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

      const result = parts.join('.')

      // Add negative sign if needed
      return numValue < 0 ? `-${result}` : result
    },

    formatInputValue(inputValue) {
      // Remove all non-numeric characters except decimal point and minus
      let cleanValue = inputValue.replace(/[^0-9.-]/g, '')

      // Handle negative numbers
      if (!this.isAllowNegativeNumbers) {
        cleanValue = cleanValue.replace(/-/g, '')
      } else {
        // Only allow one minus at the beginning
        const minusCount = (cleanValue.match(/-/g) || []).length
        if (minusCount > 1) {
          cleanValue = cleanValue.replace(/-/g, '')
          if (inputValue.startsWith('-')) {
            cleanValue = `-${cleanValue}`
          }
        } else if (cleanValue.includes('-') && !cleanValue.startsWith('-')) {
          cleanValue = cleanValue.replace('-', '')
        }
      }

      // Only allow one decimal point
      const decimalCount = (cleanValue.match(/\./g) || []).length
      if (decimalCount > 1) {
        const firstDecimalIndex = cleanValue.indexOf('.')
        cleanValue = cleanValue.substring(0, firstDecimalIndex + 1)
                    + cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '')
      }

      // Limit decimal places
      const decimalIndex = cleanValue.indexOf('.')
      if (decimalIndex !== -1 && cleanValue.length > decimalIndex + this.decimalPlaces + 1) {
        cleanValue = cleanValue.substring(0, decimalIndex + this.decimalPlaces + 1)
      }

      return cleanValue
    },

    handleInput(event) {
      const inputValue = event.target.value
      const formattedInput = this.formatInputValue(inputValue)

      // Update display with formatted input while typing
      if (this.isFocused) {
        this.displayValue = formattedInput
      }

      // Parse the numeric value
      const numericValue = parseFloat(formattedInput)

      if (!Number.isNaN(numericValue)) {
        this.internalValue = numericValue
        this.$emit('input', numericValue)

        // Call custom onInput if provided
        if (this.onInput) {
          this.onInput(numericValue)
        }
      } else if (formattedInput === '' || formattedInput === '-') {
        this.internalValue = null
        this.$emit('input', null)

        if (this.onInput) {
          this.onInput(null)
        }
      }
    },

    handleBlur() {
      this.isFocused = false
      // On blur, reformat to show proper formatting
      if (this.internalValue !== null && this.internalValue !== undefined) {
        this.displayValue = this.formatDisplayValue(this.internalValue)
      } else if (this.displayValue === '' || this.displayValue === '-') {
        this.displayValue = ''
      }
    },

    handleFocus() {
      this.isFocused = true
    },

    // eslint-disable-next-line consistent-return
    handleKeyPress(event) {
      if (!this.isAllowSpecialCharacters) {
        removeSpecialChar(event)
      }
      if (!this.isAllowNegativeNumbers) {
        removeNegativeNumber(event)
      }

      // Allow only numbers, decimal point, and minus
      const char = String.fromCharCode(event.which)
      const allowedChars = /[0-9.-]/

      if (!allowedChars.test(char)) {
        event.preventDefault()
        return false
      }
    },

    // eslint-disable-next-line consistent-return
    disableCopyPaste(event) {
      if (!this.isAllowSpecialCharacters || !this.isAllowCopyAndPaste) {
        const kCode = event.keyCode || event.charCode
        if (kCode === 86 || kCode === 0) {
          this.alertInfo({ text: 'Copy and paste not allowed' })
          return false
        }
      }
    },
  },
}
</script>
